'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Globe, Eye, EyeOff, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { signIn } from '@/lib/auth/client';
import { ensureSessionReady } from '@/lib/auth/ensure-session-ready';
import { useSession } from '@/hooks/useSession';

const SESSION_HYDRATION_TIMEOUT_MS = 4_000;

export default function LoginPage() {
  const router = useRouter();
  const session = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryable, setRetryable] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRetryable(false);
    setLoading(true);
    try {
      const result = await signIn({ email, password });
      if (!result.ok) {
        setError(result.error ?? 'Unable to sign in');
        if (result.retryable) {
          setRetryable(true);
          // Auto-retry countdown
          setRetryCountdown(10);
          const interval = setInterval(() => {
            setRetryCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(interval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
        setLoading(false);
        return;
      }
      const hydrated = await Promise.race([
        ensureSessionReady(session, {
          attempts: 2,
          initialDelayMs: 120,
        }),
        new Promise<boolean>((resolve) => {
          window.setTimeout(() => resolve(false), SESSION_HYDRATION_TIMEOUT_MS);
        }),
      ]);
      if (!hydrated) {
        setError('Signed in, but the session could not be verified yet. Please retry in a moment.');
        setRetryable(true);
        return;
      }
      router.replace('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to sign in');
    } finally {
      setLoading(false);
    }
  };

  // Auto-retry when countdown expires and the error is retryable
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (retryCountdown === 0 && retryable && !loading && email && password) {
      const timer = setTimeout(() => {
        formRef.current?.requestSubmit();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [retryCountdown, retryable, loading, email, password]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex">
      {/* Left - Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 h-64 w-64 rounded-full bg-white blur-[100px]" />
          <div className="absolute bottom-20 right-20 h-80 w-80 rounded-full bg-white blur-[120px]" />
        </div>
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 text-white backdrop-blur-sm">
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">GlonexTrack</span>
              <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-emerald-100">Telematics Intelligence</p>
            </div>
          </Link>

          {/* Main text */}
          <div className="max-w-md">
            <h2 className="text-3xl font-bold text-white leading-tight">
              Fleet intelligence at your fingertips
            </h2>
            <p className="mt-4 text-base leading-relaxed text-emerald-50/90">
              Access live tracking, camera feeds, driver safety scores, reports, and AI-powered
              insights from a single unified dashboard.
            </p>
            <div className="mt-8 space-y-3">
              {[
                'Real-time GPS with sub-second updates',
                'Quad-camera HLS streaming & ADAS',
                '100+ automated reports & analytics',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">
                    <ShieldCheck className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-sm text-emerald-50">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['TM', 'NK', 'DB'].map((initials) => (
                <div key={initials} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-xs font-bold text-white ring-2 ring-emerald-500">
                  {initials}
                </div>
              ))}
            </div>
            <p className="text-sm text-emerald-100">
              <span className="font-semibold text-white">300+</span> organizations trust GlonexTrack
            </p>
          </div>
        </div>
      </div>

      {/* Right - Login form */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        {/* Mobile logo */}
        <Link href="/" className="mb-8 flex items-center gap-2 lg:hidden">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white">
            <Globe className="h-4 w-4" />
          </div>
          <span className="text-lg font-bold text-slate-900">GlonexTrack</span>
        </Link>

        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:text-left">
            <h1 className="text-2xl font-bold text-slate-900">Sign in to your account</h1>
            <p className="mt-2 text-sm text-slate-500">
              Enter your credentials to access your fleet dashboard.
            </p>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Email address</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
                autoComplete="email"
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <Link href="/auth/forgot-password" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className={`rounded-xl border px-4 py-2.5 text-sm ${retryable ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-red-200 bg-red-50 text-red-600'}`}>
                <p>{error}</p>
                {retryable && retryCountdown > 0 && (
                  <p className="mt-1 text-xs text-amber-500">Retrying automatically in {retryCountdown}s...</p>
                )}
                {retryable && retryCountdown === 0 && loading && (
                  <p className="mt-1 text-xs text-amber-500">Retrying...</p>
                )}
              </div>
            )}

            <Button type="submit" variant="accent" size="lg" className="w-full rounded-xl" disabled={loading}>
              {loading ? (
                <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Signing in...</>
              ) : (
                <>Sign In <ArrowRight className="h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              Don&apos;t have an account?{' '}
              <Link href="/signup" className="font-semibold text-emerald-600 hover:text-emerald-700">
                Start your free trial
              </Link>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> POPIA</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> GDPR</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-500" /> End-to-end encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
}
